import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { CatalogFacade } from '../../../facades/catalog.facade';
import { MathUtils } from '../../../utils/math.utils';
import { AnalyticsService } from '../../../analytics/analytics.service';
import { EProductDetails } from '../../../configurations/product-details';
import { environment } from '../../../../environments/environment';
import { AppUtils } from '../../../utils/app.utils';
import { PriceUtils } from '../../../utils/price.utils';
import { EFeatureToggles, EPriceVariant } from '../../../configurations/common';
import { ConfigurationFacade } from '../../../facades/configuration.facade';
import { Observable, Subject } from 'rxjs';
import { ICart, IAddToCartAdditionalFields } from '../../../models/cart.models';
import { take, takeUntil } from 'rxjs/operators';
import { MarketingFacade } from '../../../facades/marketing.facade';
import { IProductLabel } from '../../../models/abstract-product.models';
import { IPrice, ISystemDetails } from '../../../models/common.models';
import { ISimpleInstallBaseProduct } from '../../../models/installedbase.models';
import { IBaseWishlist } from '../../../models/wishlist.models';

@Component({
  selector: 'app-basic-product-pdp',
  templateUrl: './basic-product-component-pdp.component.html',
  styleUrls: ['./basic-product-component-pdp.component.scss'],
  standalone: false,
})
export class BasicProductPdpComponent implements OnInit {
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly catalogFacade: CatalogFacade = inject(CatalogFacade);
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private readonly marketingFacade: MarketingFacade = inject(MarketingFacade);

  isMyInstalledBaseFlowEnabled$: Observable<boolean> = new Observable<boolean>();
  isProductDiscontinuedStatusEnabled$: Observable<boolean> = new Observable<boolean>();
  isProductVariantsEnabled$: Observable<boolean> = this.configurationFacade.isFeatureEnabled(EFeatureToggles.PRODUCT_VARIANTS);
  isQuantityValid: boolean = true;
  isExchangeableFeatureActive: boolean = false;
  selectedPriceType: EPriceVariant = EPriceVariant.WITHOUT_REPLACEMENT;

  currentCart!: ICart;
  openZoomedPicture: boolean = false;
  priceUtils: typeof PriceUtils = PriceUtils;
  priceVariant: typeof EPriceVariant = EPriceVariant;
  systemDetails!: ISystemDetails;

  @Input() abstractProduct: any;
  @Input() addNewModalActive!: boolean;
  @Input() concreteSku: string | null = null;
  @Input() defaultImg!: string;
  @Input() defaultPrice!: IPrice;
  @Input() housingFee: string | null = null;
  @Input() listPriceWithoutReplacement: IPrice | null = null;
  @Input() listPriceWithReplacement: IPrice | null = null;
  @Input() yourPriceWithoutReplacement: IPrice | null = null;
  @Input() yourPriceWithReplacement: IPrice | null = null;
  @Input() displayConfig: EProductDetails[] = [];
  @Input() installBaseProducts: ISimpleInstallBaseProduct[] = [];
  @Input() isAddToCartInProgress$: Observable<boolean> = new Observable<boolean>();
  @Input() isInStore!: boolean;
  @Input() labels: IProductLabel[] = [];
  @Input() largeUrls: string[] = [];
  @Input() loadingCartDataInProgress!: boolean;
  @Input() mainPicture!: string;
  @Input() productDetailsEnum: typeof EProductDetails = EProductDetails;
  @Input() productInfoLoaded!: boolean;
  @Input() productQuantity!: number;
  @Input() secondPrice: IPrice | null = null;
  @Input() showPriceDisclaimer!: boolean;
  @Input() sku!: string;
  @Input() smallUrls: string[] = [];
  @Input() wasRedirectedFromEquipmentPage!: boolean;
  @Input() wishlists: IBaseWishlist[] = [];
  @Input() isWishlistsActive: boolean = false;
  @Input() isProductTagsActive: boolean = false;
  @Input() isPredefinedEquipmentFlowEnabled: boolean = false;
  @Input() isDeliveryAvailabilityEnabled: boolean = false;
  @Input() isEstimatedDeliveryEnabled: boolean = false;
  @Output() addNewModalWishlist: EventEmitter<void> = new EventEmitter<void>();
  @Output() addProductToCartSubmit: EventEmitter<IAddToCartAdditionalFields> = new EventEmitter<IAddToCartAdditionalFields>();
  @Output() productQuantityChange: EventEmitter<number> = new EventEmitter<number>();

  private readonly unsubscribe$: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.selectIsExchangeableFeatureEnabled();
    this.selectCurrentCart();
    this.isProductDiscontinuedStatusEnabled$ = this.configurationFacade.isFeatureEnabled(EFeatureToggles.PRODUCT_DISCONTINUED_STATUS);
    this.isMyInstalledBaseFlowEnabled$ = this.configurationFacade.isFeatureEnabled(EFeatureToggles.MY_INSTALLED_BASE_FLOW);
  }

  trackInstalledBase(installedBase: ISimpleInstallBaseProduct): void {
    this.catalogFacade.setInstalledBase(installedBase);
    this.analyticsService.trackInstalledBase(installedBase.attributes.materialName, installedBase.id ?? '');
  }

  tracking(name: string): void {
    this.analyticsService.setProducts(this.abstractProduct);
    this.analyticsService.trackAccordion(name);
  }

  selectCurrentCart(): void {
    this.marketingFacade.selectCart().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(cart => {
      if (cart) {
        this.currentCart = cart;
        if (cart.attributes?.systemDetails) {
          this.systemDetails = cart.attributes.systemDetails;
        }
      }
    });
  }

  addToWishlist(): void {
    this.addNewModalWishlist.emit();
  }

  displayProductDetail(section: EProductDetails): boolean {
    return this.displayConfig.includes(section);
  }

  setLargeUrl(index: number): void {
    this.mainPicture = this.largeUrls[index];
  }

  changeProductQuantity(productQuantity: number | null): void {
    this.checkIfPositiveInteger(productQuantity);
    if (this.isQuantityValid) {
      this.productQuantityChange.emit(productQuantity ?? 0);
    }
  }

  private checkIfPositiveInteger(productQuantity: number | null): void {
    this.isQuantityValid = MathUtils.checkIfNumeric(productQuantity) && (productQuantity ?? 0) > 0;
  }

  openZoomPicture(): void {
    if (this.mainPicture !== this.defaultImg) {
      this.openZoomedPicture = !this.openZoomedPicture;
      if (this.openZoomedPicture) {
        this.analyticsService.setProducts(this.abstractProduct);
        this.analyticsService.trackProduct('image.enlarge', this.abstractProduct);
      }
    }
  }

  addProductToCart(): void {
    this.isProductVariantsEnabled$.pipe(take(1)).subscribe((isEnabled: boolean) => {
      const suffix: string = this.selectedPriceType === EPriceVariant.WITH_REPLACEMENT ? '_W' : '_S';
      this.addProductToCartSubmit.emit(isEnabled ? {sku: `${this.concreteSku || this.sku}${suffix}`} : {});
    });
  }

  isExcludeTaxActive(): boolean {
    const currentStore: any = AppUtils.getCurrentStore();
    return !environment.features.excludeTax.includes(currentStore.storeId);
  }

  /**
   * Checks if the exchangeable tag should be shown based on the product attributes and feature toggle.
   * @returns {boolean}
   */
  isExchangeableTagShown(): boolean {
    if (this.abstractProduct && this.isExchangeableFeatureActive) {
      return !!this.abstractProduct.attributes.mcm_webshop_customer_price;
    }
    return false;
  }

  /**
   * Selects whether the exchangeable feature is enabled.
   */
  selectIsExchangeableFeatureEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.EXCHANGEABLE_ITEM_TAG)
      .pipe(take(1))
      .subscribe(isExchangeableEnabled => {
        this.isExchangeableFeatureActive = isExchangeableEnabled;
      });
  }

  /**
   * Determines whether a price block (without-replacement or with-replacement) should be displayed.
   *
   * The block is shown only if the corresponding variant prices are available
   * (extracted from concrete-product-prices with _S or _W suffix).
   *
   * @param {EPriceVariant} priceVariant - The price variant type.
   * @returns {boolean} True if the price block should be displayed, false otherwise.
   */
  protected isPriceBlockDisplayed(priceVariant: EPriceVariant): boolean {
    if (priceVariant === EPriceVariant.WITHOUT_REPLACEMENT) {
      return !!this.listPriceWithoutReplacement || !!this.yourPriceWithoutReplacement;
    } else if (priceVariant === EPriceVariant.WITH_REPLACEMENT) {
      return !!this.listPriceWithReplacement || !!this.yourPriceWithReplacement;
    }
    return false;
  }

  /**
   * Determines whether only a single price variant block is available.
   * Used to hide radio buttons when there is no choice to make.
   *
   * @returns {boolean} true if exactly one price block is displayed
   */
  protected isSinglePriceBlock(): boolean {
    return this.isPriceBlockDisplayed(EPriceVariant.WITHOUT_REPLACEMENT) !== this.isPriceBlockDisplayed(EPriceVariant.WITH_REPLACEMENT);
  }
}
